import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  Alert,
  SelectChangeEvent
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from 'app/store';
import { UIMappedInvoice, Shift, Invoice } from '../../../../types/ndisSection/invoiceTypes';
import { editInvoice, fetchSingleInvoice } from 'slices/NdisSlices/finance/InvoiceSlice';
import { mapToUIMappedInvoice } from './FinanceTab';

interface InvoiceFormProps {
  invoice: Invoice | null;
  onSubmit: (data: UIMappedInvoice) => void;
  onCancel: () => void;
  isSmallScreen: boolean;
  fortnightDate: string;
}

const InvoiceForm: React.FC<InvoiceFormProps> = ({
  invoice,
  onSubmit,
  onCancel,
  isSmallScreen,
  fortnightDate
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const selectedProjectId = useSelector(
    (state: RootState) => (state as any).homeTilesServices?.selectedProjectId || ''
  );
  const selectedServiceTitle = useSelector(
    (state: RootState) => state.serviceSelected.selectedServiceTitle || ''
  );
  const { current_invoice, loading, error } = useSelector(
    (state: RootState) => state.invoice || {}
  );

  const [formData, setFormData] = useState<UIMappedInvoice>({
    id: '',
    invoiceNumber: '',
    customerName: '',
    address: '',
    cityStateZip: '',
    date: new Date().toISOString().split('T')[0],
    issuedPeriodDate_start: '',
    issuedPeriodDate_end: '',
    dueDate: new Date(new Date().setDate(new Date().getDate() + 30)).toISOString().split('T')[0],
    totalAmount: 0,
    status: 'unpaid',
    paymentDetails: {
      BSB: '',
      Account: '',
      ABN: '',
      NDIS_Registration_Number: '',
      Contact: ''
    },
    invoiceItems: [],
    client_reference: '',
    registrationnumber: '',
    ndisnumber: '',
    authorisedby: '',
    participantapproved: false,
    inkindfundingprogram: '',
    claimtype: '',
    gstcode: '',
    supportnumber: '',
    Support_Item_Name: '',
    shifts: [],
    summary: { total_hours: 0, total_quantity: 0, total_amount_due: 0 }
  });

  const parsedUserDetails = JSON.parse(localStorage.getItem('user-details') || '{}');
  const parsedSelectedClient = JSON.parse(localStorage.getItem('selectedClient') || '{}');

  useEffect(() => {
    console.log('Invoice prop:', invoice);
    console.log('Current invoice:', current_invoice);
    if (invoice && (invoice.id || invoice.invoice_number) && !current_invoice) {
      const filterDate = fortnightDate
        ? new Date(fortnightDate.split(' - ')[0].split(': ')[1].split('/').reverse().join('-'))
            .toISOString()
            .split('T')[0]
        : new Date().toISOString().split('T')[0];


      const clientName = invoice.customer_name || parsedSelectedClient.label || '';
      const payload = {
        user_whoRequested_id: parsedUserDetails.name || '',
        group_id: selectedProjectId || '',
        service_name_id: selectedServiceTitle || '',
        nested_component_id: 'Invoice',
        invoice_id: invoice.invoice_number || invoice.id || '',
        client_key: invoice.client_key  || '',
        client_name: clientName,
        filter_date: filterDate
      };
      console.log('Fetching invoice with payload:', payload);
      dispatch(fetchSingleInvoice(payload));
    } else if (invoice) {
      console.log('Setting form data from invoice prop:', invoice);
      setFormData(mapToUIMappedInvoice(invoice));
    }
  }, [
    invoice,
    dispatch,
    current_invoice,
    parsedUserDetails.name,
    parsedSelectedClient.label,
    selectedProjectId,
    selectedServiceTitle,
    fortnightDate
  ]);

  useEffect(() => {
    if (current_invoice) {
      console.log('Setting form data from current_invoice:', current_invoice);
      setFormData(mapToUIMappedInvoice(current_invoice));
    }
  }, [current_invoice]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;
    const checked = 'checked' in e.target ? e.target.checked : undefined;
    setFormData({
      ...formData,
      [name]: checked !== undefined ? checked : value
    });
  };

  const handlePaymentDetailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      paymentDetails: { ...formData.paymentDetails, [name]: value }
    });
  };

  const handleShiftChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updatedShifts = [...formData.shifts];
    updatedShifts[index] = {
      ...updatedShifts[index],
      [name]:
        name === 'hours' ||
        name === 'unit_price' ||
        name === 'quantity' ||
        name === 'Total_amount' ||
        name === 'price'
          ? parseFloat(value) || 0
          : value
    };
    updatedShifts[index].Total_amount =
      (updatedShifts[index].unit_price ?? 0) * (updatedShifts[index].quantity ?? 0);
    updatedShifts[index].price = updatedShifts[index].unit_price ?? 0;
    const updatedInvoiceItems = updatedShifts.map((shift) => ({
      description: shift.shift_title ?? '',
      quantity: shift.quantity ?? 0,
      price: shift.Total_amount ?? 0,
      status: 'unpaid' as 'paid' | 'unpaid' | 'completed'
    }));
    const summary = updatedShifts.reduce(
      (acc: UIMappedInvoice['summary'], shift: Shift) => ({
        total_hours: acc.total_hours + (shift.hours ?? 0),
        total_quantity: acc.total_quantity + (shift.quantity ?? 0),
        total_amount_due: acc.total_amount_due + (shift.Total_amount ?? 0)
      }),
      { total_hours: 0, total_quantity: 0, total_amount_due: 0 }
    );
    setFormData({
      ...formData,
      shifts: updatedShifts,
      invoiceItems: updatedInvoiceItems,
      summary,
      totalAmount: summary.total_amount_due
    });
  };

  const addShift = () => {
    const newShift: Shift = {
      shift_title: '',
      supports_delivered_from: '',
      supports_delivered_to: '',
      issued_period_date_start: '',
      issued_period_date_end: '',
      hours: 0,
      unit_price: 0,
      price: 0,
      quantity: 1,
      Total_amount: 0,
      support_number: '',
      claimreference: '',
      support_item_name: ''
    };
    setFormData({
      ...formData,
      shifts: [...formData.shifts, newShift],
      invoiceItems: [
        ...formData.invoiceItems,
        { description: '', quantity: 1, price: 0, status: 'unpaid' }
      ]
    });
  };

  const removeShift = (index: number) => {
    const updatedShifts = formData.shifts.filter((_: Shift, i: number) => i !== index);
    const updatedInvoiceItems = formData.invoiceItems.filter(
      (_: UIMappedInvoice['invoiceItems'][number], i: number) => i !== index
    );
    const summary = updatedShifts.reduce(
      (acc: UIMappedInvoice['summary'], shift: Shift) => ({
        total_hours: acc.total_hours + (shift.hours ?? 0),
        total_quantity: acc.total_quantity + (shift.quantity ?? 0),
        total_amount_due: acc.total_amount_due + (shift.Total_amount ?? 0)
      }),
      { total_hours: 0, total_quantity: 0, total_amount_due: 0 }
    );
    setFormData({
      ...formData,
      shifts: updatedShifts,
      invoiceItems: updatedInvoiceItems,
      summary,
      totalAmount: summary.total_amount_due
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const invoiceData = mapToInvoice(formData);
    const filterDate = fortnightDate
      ? new Date(fortnightDate.split(' - ')[0].split(': ')[1].split('/').reverse().join('-'))
          .toISOString()
          .split('T')[0]
      : new Date().toISOString().split('T')[0];
  

    const payload = {
      user_whoRequested_id: parsedUserDetails.name || '',
      group_id: selectedProjectId || '',
      service_name_id: (selectedServiceTitle || '').replace(' ', '_'),
      nested_component_id: 'Invoice',
      invoice_id: formData.id,
      client_name: formData.customerName,
      filter_date: filterDate,
      updated_invoice: invoiceData
    };
    console.log('Submitting invoice with payload:', payload);
    if (invoice && (invoice.id || invoice.invoice_number)) {
      dispatch(editInvoice(payload));
    }
    onSubmit(formData);
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        p: isSmallScreen ? 1 : 2,
        borderRadius: 2
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 'bold',
          textAlign: 'center',
          mb: 3,
          fontSize: isSmallScreen ? '1.3rem' : '1.5rem',
          color: 'primary.main'
        }}
      >
        {invoice ? 'Edit Invoice' : 'Create Invoice'}
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}
            >
              Basic Information
            </Typography>
            <Box
              sx={{
                p: isSmallScreen ? 1 : 2,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Client Reference"
                    name="client_reference"
                    value={formData.client_reference}
                    onChange={handleChange}
                    required
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Invoice Number"
                    name="invoiceNumber"
                    value={formData.invoiceNumber}
                    onChange={handleChange}
                    required
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Registration Number"
                    name="registrationnumber"
                    value={formData.registrationnumber}
                    onChange={handleChange}
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="NDIS Number"
                    name="ndisnumber"
                    value={formData.ndisnumber}
                    onChange={handleChange}
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Authorised By"
                    name="authorisedby"
                    value={formData.authorisedby}
                    onChange={handleChange}
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl component="fieldset">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography sx={{ mr: 2, fontSize: '0.875rem' }}>
                        Participant Approved
                      </Typography>
                      <Switch
                        name="participantapproved"
                        checked={formData.participantapproved}
                        onChange={handleChange}
                      />
                    </Box>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="In-Kind Funding Program"
                    name="inkindfundingprogram"
                    value={formData.inkindfundingprogram}
                    onChange={handleChange}
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: '0.875rem' }}>Claim Type</InputLabel>
                    <Select
                      name="claimtype"
                      value={formData.claimtype}
                      onChange={handleChange}
                      required
                      sx={{ fontSize: '0.875rem' }}
                    >
                      <MenuItem value="Standard">Standard</MenuItem>
                      <MenuItem value="Non-Standard">Non-Standard</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="GST Code"
                    name="gstcode"
                    value={formData.gstcode}
                    onChange={handleChange}
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}
            >
              Participant Information
            </Typography>
            <Box
              sx={{
                p: isSmallScreen ? 1 : 2,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Customer Name"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleChange}
                    required
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City, State, Zip"
                    name="cityStateZip"
                    value={formData.cityStateZip}
                    onChange={handleChange}
                    required
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contact"
                    name="Contact"
                    value={formData.paymentDetails.Contact}
                    onChange={handlePaymentDetailChange}
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}
            >
              Payment Details
            </Typography>
            <Box
              sx={{
                p: isSmallScreen ? 1 : 2,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="BSB"
                    name="BSB"
                    value={formData.paymentDetails.BSB}
                    onChange={handlePaymentDetailChange}
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Account Number"
                    name="Account"
                    value={formData.paymentDetails.Account}
                    onChange={handlePaymentDetailChange}
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="ABN"
                    name="ABN"
                    value={formData.paymentDetails.ABN}
                    onChange={handlePaymentDetailChange}
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="NDIS Registration Number"
                    name="NDIS_Registration_Number"
                    value={formData.paymentDetails.NDIS_Registration_Number}
                    onChange={handlePaymentDetailChange}
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}
            >
              Dates
            </Typography>
            <Box
              sx={{
                p: isSmallScreen ? 1 : 2,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Issued Date"
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    required
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Period Start"
                    type="date"
                    name="issuedPeriodDate_start"
                    value={formData.issuedPeriodDate_start}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    required
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Period End"
                    type="date"
                    name="issuedPeriodDate_end"
                    value={formData.issuedPeriodDate_end}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    required
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Due Date"
                    type="date"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}
            >
              Shifts
            </Typography>
            <Box
              sx={{
                p: isSmallScreen ? 1 : 2,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              {formData.shifts.map((shift: Shift, index: number) => (
                <FormGroup
                  key={index}
                  sx={{
                    mb: 2,
                    p: isSmallScreen ? 1 : 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.default'
                  }}
                >
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Shift Title"
                        name="shift_title"
                        value={shift.shift_title ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleShiftChange(index, e)
                        }
                        required
                        size="small"
                        sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Supports Delivered From"
                        type="date"
                        name="supports_delivered_from"
                        value={shift.supports_delivered_from ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleShiftChange(index, e)
                        }
                        InputLabelProps={{ shrink: true }}
                        required
                        size="small"
                        sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Supports Delivered To"
                        type="date"
                        name="supports_delivered_to"
                        value={shift.supports_delivered_to ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleShiftChange(index, e)
                        }
                        InputLabelProps={{ shrink: true }}
                        required
                        size="small"
                        sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Period Start"
                        type="date"
                        name="issued_period_date_start"
                        value={shift.issued_period_date_start ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleShiftChange(index, e)
                        }
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Period End"
                        type="date"
                        name="issued_period_date_end"
                        value={shift.issued_period_date_end ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleShiftChange(index, e)
                        }
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        label="Hours"
                        type="number"
                        name="hours"
                        value={shift.hours ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleShiftChange(index, e)
                        }
                        required
                        size="small"
                        inputProps={{ step: '0.01', min: 0 }}
                        sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        label="Unit Price"
                        type="number"
                        name="unit_price"
                        value={shift.unit_price ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleShiftChange(index, e)
                        }
                        required
                        size="small"
                        inputProps={{ step: '0.01', min: 0 }}
                        sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        label="Price"
                        type="number"
                        name="price"
                        value={shift.price ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleShiftChange(index, e)
                        }
                        required
                        size="small"
                        inputProps={{ step: '0.01', min: 0 }}
                        sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        label="Quantity"
                        type="number"
                        name="quantity"
                        value={shift.quantity ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleShiftChange(index, e)
                        }
                        required
                        size="small"
                        inputProps={{ min: 1 }}
                        sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        label="Total Amount"
                        type="number"
                        name="Total_amount"
                        value={shift.Total_amount ?? ''}
                        InputProps={{ readOnly: true }}
                        size="small"
                        sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Support Number"
                        name="support_number"
                        value={shift.support_number ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleShiftChange(index, e)
                        }
                        size="small"
                        sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Claim Reference"
                        name="claimreference"
                        value={shift.claimreference ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleShiftChange(index, e)
                        }
                        size="small"
                        sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Support Item Name"
                        name="support_item_name"
                        value={shift.support_item_name ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleShiftChange(index, e)
                        }
                        size="small"
                        sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ textAlign: 'center' }}>
                      {formData.shifts.length > 1 && (
                        <IconButton
                          onClick={() => removeShift(index)}
                          color="error"
                          size="small"
                          sx={{
                            border: '1px solid',
                            borderColor: 'error.main',
                            borderRadius: 1
                          }}
                        >
                          <Remove fontSize="small" />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                </FormGroup>
              ))}
              <Button
                startIcon={<Add />}
                onClick={addShift}
                variant="outlined"
                size="small"
                fullWidth={isSmallScreen}
                sx={{
                  mt: 1,
                  fontSize: '0.75rem',
                  textTransform: 'none'
                }}
              >
                Add Shift
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}
            >
              Summary
            </Typography>
            <Box
              sx={{
                p: isSmallScreen ? 1 : 2,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Total Hours"
                    value={formData.summary.total_hours.toFixed(2)}
                    InputProps={{ readOnly: true }}
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Total Quantity"
                    value={formData.summary.total_quantity}
                    InputProps={{ readOnly: true }}
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Total Amount Due"
                    value={formData.summary.total_amount_due.toFixed(2)}
                    InputProps={{ readOnly: true }}
                    size="small"
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1.5,
                mt: 1,
                flexDirection: isSmallScreen ? 'column' : 'row'
              }}
            >
              <Button
                variant="outlined"
                onClick={onCancel}
                size={isSmallScreen ? 'medium' : 'small'}
                fullWidth={isSmallScreen}
                sx={{
                  fontSize: '0.75rem',
                  textTransform: 'none'
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size={isSmallScreen ? 'medium' : 'small'}
                fullWidth={isSmallScreen}
                sx={{
                  fontSize: '0.75rem',
                  textTransform: 'none'
                }}
                disabled={loading}
              >
                {invoice ? 'Update Invoice' : 'Create Invoice'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default InvoiceForm;

export const mapToInvoice = (uiInvoice: UIMappedInvoice): Invoice => ({
  id: uiInvoice.id,
  invoice_number: uiInvoice.invoiceNumber,
  customer_name: uiInvoice.customerName,
  address: uiInvoice.address,
  city_state_zip: uiInvoice.cityStateZip,
  issued_date: uiInvoice.date,
  issued_period_date_start: uiInvoice.issuedPeriodDate_start,
  issued_period_date_end: uiInvoice.issuedPeriodDate_end,
  due_date: uiInvoice.dueDate,
  bsb: uiInvoice.paymentDetails.BSB,
  account: uiInvoice.paymentDetails.Account,
  abn: uiInvoice.paymentDetails.ABN,
  contact: uiInvoice.paymentDetails.Contact,
  gst_code: uiInvoice.gstcode,
  client_reference: uiInvoice.client_reference,
  registration_number: uiInvoice.registrationnumber,
  ndis_number: uiInvoice.ndisnumber,
  authorised_by: uiInvoice.authorisedby,
  participant_approved: uiInvoice.participantapproved,
  in_kind_funding_program: uiInvoice.inkindfundingprogram,
  claim_type: uiInvoice.claimtype,
  shifts: uiInvoice.shifts.map((shift) => ({
    ...shift,
    price: shift.unit_price ?? 0,
    issued_period_date_start: shift.issued_period_date_start || shift.supports_delivered_from,
    issued_period_date_end: shift.issued_period_date_end || shift.supports_delivered_to
  })),
  summary: {
    total_hours: uiInvoice.summary.total_hours,
    total_quantity: uiInvoice.summary.total_quantity,
    total_amount_due: uiInvoice.summary.total_amount_due
  },
  Hours: uiInvoice.summary.total_hours,
  Amount: uiInvoice.summary.total_amount_due
});

// import React, { useEffect } from 'react';
// import {
//   Box,
//   Button,
//   CircularProgress,
//   FormControl,
//   FormGroup,
//   Grid,
//   IconButton,
//   InputLabel,
//   MenuItem,
//   Select,
//   Switch,
//   TextField,
//   Typography,
//   Alert
// } from '@mui/material';
// import { Add, Remove } from '@mui/icons-material';
// import { useDispatch, useSelector } from 'react-redux';
// import { RootState, AppDispatch } from 'app/store';
// import { UIMappedInvoice, Shift, Invoice } from '../../../../types/ndisSection/invoiceTypes';
// import { editInvoice, fetchSingleInvoice } from 'slices/NdisSlices/finance/InvoiceSlice';
// import { useForm, Controller, useFieldArray } from 'react-hook-form';
// import { yupResolver } from '@hookform/resolvers/yup';
// import * as yup from 'yup';
// import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// import { SelectChangeEvent } from '@mui/material';

// // Validation schema aligned with UIMappedInvoice type
// const invoiceSchema = yup.object().shape({
//   id: yup.string().required('ID is required'),
//   invoiceNumber: yup.string().required('Invoice number is required'),
//   customerName: yup.string().required('Customer name is required'),
//   address: yup.string().required('Address is required'),
//   cityStateZip: yup.string().required('City/State/Zip is required'),
//   date: yup.string().required('Issued date is required'),
//   issuedPeriodDate_start: yup.string().required('Period start date is required'),
//   issuedPeriodDate_end: yup
//     .string()
//     .required('Period end date is required')
//     .test('date-after-start', 'End date must be after start date', function (value) {
//       const startDate = this.parent.issuedPeriodDate_start;
//       if (!value || !startDate) return true;
//       return new Date(value) >= new Date(startDate);
//     }),
//   dueDate: yup.string().required('Due date is required'),
//   totalAmount: yup.number().required('Total amount is required'),
//   status: yup.string().oneOf(['paid', 'unpaid', 'completed']).required('Status is required'),
//   paymentDetails: yup
//     .object()
//     .shape({
//       BSB: yup.string().required('BSB is required'),
//       Account: yup.string().required('Account is required'),
//       ABN: yup.string().required('ABN is required'),
//       NDIS_Registration_Number: yup.string().required('NDIS Registration Number is required'),
//       Contact: yup.string().required('Contact is required')
//     })
//     .required(),
//   invoiceItems: yup
//     .array()
//     .of(
//       yup.object().shape({
//         description: yup.string().required('Description is required'),
//         quantity: yup
//           .number()
//           .required('Quantity is required')
//           .min(1, 'Quantity must be at least 1'),
//         price: yup.number().required('Price is required').min(0, 'Price must be positive'),
//         status: yup.string().oneOf(['paid', 'unpaid', 'completed']).required('Status is required')
//       })
//     )
//     .required(),
//   client_reference: yup.string().required('Client reference is required'),
//   registrationnumber: yup.string().required('Registration number is required'),
//   ndisnumber: yup.string().required('NDIS number is required'),
//   authorisedby: yup.string().required('Authorised by is required'),
//   participantapproved: yup.boolean().required('Participant approved is required'),
//   inkindfundingprogram: yup.string().required('In-kind funding program is required'),
//   claimtype: yup.string().required('Claim type is required'),
//   gstcode: yup.string().required('GST code is required'),
//   supportnumber: yup.string().required('Support number is required'),
//   Support_Item_Name: yup.string().required('Support item name is required'),
//   shifts: yup
//     .array()
//     .of(
//       yup.object().shape({
//         shift_title: yup.string(),
//         supports_delivered_from: yup.string(),
//         supports_delivered_to: yup
//           .string()
//           .test('date-after-from', 'End date must be after start date', function (value) {
//             const fromDate = this.parent.supports_delivered_from;
//             if (!value || !fromDate) return true;
//             return new Date(value) >= new Date(fromDate);
//           }),
//         issued_period_date_start: yup.string(),
//         issued_period_date_end: yup.string(),
//         support_number: yup.string(),
//         claimreference: yup.string(),
//         support_item_name: yup.string(),
//         hours: yup.number().min(0, 'Hours must be positive'),
//         unit_price: yup.number().min(0, 'Price must be positive'),
//         quantity: yup.number().min(1, 'Quantity must be at least 1'),
//         price: yup.number().min(0, 'Price must be positive'),
//         Total_amount: yup.number().min(0, 'Amount must be positive')
//       })
//     )
//     .min(1, 'At least one shift is required')
//     .required(),
//   summary: yup
//     .object()
//     .shape({
//       total_hours: yup.number().required('Total hours is required'),
//       total_quantity: yup.number().required('Total quantity is required'),
//       total_amount_due: yup.number().required('Total amount due is required')
//     })
//     .required(),
//   avatar: yup.string()
// });

// interface InvoiceFormProps {
//   invoice: Invoice | null;
//   onSubmit: (data: UIMappedInvoice) => void;
//   onCancel: () => void;
//   isSmallScreen: boolean;
//   fortnightDate: string;
// }

// const InvoiceForm: React.FC<InvoiceFormProps> = ({
//   invoice,
//   onSubmit,
//   onCancel,
//   isSmallScreen,
//   fortnightDate
// }) => {
//   const dispatch = useDispatch<AppDispatch>();
//   const selectedProjectId = useSelector(
//     (state: RootState) => (state as any).homeTilesServices?.selectedProjectId || ''
//   );
//   const selectedServiceTitle = useSelector(
//     (state: RootState) => state.serviceSelected.selectedServiceTitle || ''
//   );
//   const { current_invoice, loading, error } = useSelector(
//     (state: RootState) => state.invoice || {}
//   );

//   const parsedUserDetails = JSON.parse(localStorage.getItem('user-details') || '{}');
//   const parsedSelectedClient = JSON.parse(localStorage.getItem('selectedClient') || '{}');

//   const {
//     control,
//     handleSubmit,
//     reset,
//     watch,
//     setValue,
//     formState: { errors }
//   } = useForm<UIMappedInvoice>({
//     resolver: yupResolver(invoiceSchema),
//     defaultValues: {
//       id: '',
//       invoiceNumber: '',
//       customerName: '',
//       address: '',
//       cityStateZip: '',
//       date: new Date().toISOString().split('T')[0],
//       issuedPeriodDate_start: '',
//       issuedPeriodDate_end: '',
//       dueDate: new Date(new Date().setDate(new Date().getDate() + 30)).toISOString().split('T')[0],
//       totalAmount: 0,
//       status: 'unpaid',
//       paymentDetails: {
//         BSB: '',
//         Account: '',
//         ABN: '',
//         NDIS_Registration_Number: '',
//         Contact: ''
//       },
//       invoiceItems: [],
//       client_reference: '',
//       registrationnumber: '',
//       ndisnumber: '',
//       authorisedby: '',
//       participantapproved: false,
//       inkindfundingprogram: '',
//       claimtype: '',
//       gstcode: '',
//       supportnumber: '',
//       Support_Item_Name: '',
//       shifts: [],
//       summary: { total_hours: 0, total_quantity: 0, total_amount_due: 0 },
//       avatar: ''
//     }
//   });

//   const { fields, append, remove } = useFieldArray({
//     control,
//     name: 'shifts'
//   });

//   const shifts = watch('shifts');
//   const summary = watch('summary');
// console.log('fortnightDate', fortnightDate);
//   useEffect(() => {
//     if (invoice && (invoice.id || invoice.invoice_number) && !current_invoice) {
//       const clientName = invoice.customer_name || parsedSelectedClient.label || '';

//       const filterDate = fortnightDate
//         ? fortnightDate.split(' - ')[0].split(': ')[1]
//         : new Date().toISOString().split('T')[0];

//       const payload = {
//         user_whoRequested_id: parsedUserDetails.name || '',
//         group_id: selectedProjectId || '',
//         service_name_id: selectedServiceTitle || '',
//         nested_component_id: 'Invoice',
//         invoice_id: invoice.id || invoice.invoice_number,
//         client_name: clientName,
//         filter_date: filterDate
//       };

//       console.log('Fetching invoice with payload:', payload);
//       dispatch(fetchSingleInvoice(payload));
//     } else if (invoice) {
//       console.log('Setting form data from invoice prop:', invoice);
//       reset(mapToUIMappedInvoice(invoice));
//     }
//   }, [
//     invoice,
//     dispatch,
//     current_invoice,
//     parsedUserDetails.name,
//     parsedSelectedClient.label,
//     selectedProjectId,
//     selectedServiceTitle,
//     reset,
//     fortnightDate
//   ]);
//   useEffect(() => {
//     if (current_invoice) {
//       reset(mapToUIMappedInvoice(current_invoice));
//     }
//   }, [current_invoice, reset]);

//   useEffect(() => {
//     const newSummary = shifts.reduce(
//       (acc, shift) => ({
//         total_hours: acc.total_hours + (shift.hours || 0),
//         total_quantity: acc.total_quantity + (shift.quantity || 0),
//         total_amount_due: acc.total_amount_due + (shift.Total_amount || 0)
//       }),
//       { total_hours: 0, total_quantity: 0, total_amount_due: 0 }
//     );
//     setValue('summary', newSummary);
//     setValue('totalAmount', newSummary.total_amount_due);
//   }, [shifts, setValue]);

//   const handleShiftChange = (index: number, field: keyof Shift, value: any) => {
//     if (field === 'unit_price' || field === 'quantity') {
//       const unitPrice =
//         field === 'unit_price' ? parseFloat(value) || 0 : shifts[index].unit_price || 0;
//       const quantity = field === 'quantity' ? parseFloat(value) || 0 : shifts[index].quantity || 0;
//       const totalAmount = unitPrice * quantity;

//       setValue(`shifts.${index}.unit_price`, unitPrice);
//       setValue(`shifts.${index}.quantity`, quantity);
//       setValue(`shifts.${index}.price`, unitPrice);
//       setValue(`shifts.${index}.Total_amount`, totalAmount);
//     } else {
//       setValue(`shifts.${index}.${field}`, value);
//     }
//   };

//   const addShift = () => {
//     append({
//       shift_title: '',
//       supports_delivered_from: '',
//       supports_delivered_to: '',
//       issued_period_date_start: '',
//       issued_period_date_end: '',
//       hours: 0,
//       unit_price: 0,
//       quantity: 1,
//       price: 0,
//       Total_amount: 0,
//       support_number: '',
//       claimreference: '',
//       support_item_name: ''
//     });
//   };

//   const removeShift = (index: number) => {
//     remove(index);
//   };

//   const onSubmitForm = (data: UIMappedInvoice) => {
//     const invoiceData = mapToInvoice(data);

//     const filterDate = fortnightDate
//       ? fortnightDate.split(' - ')[0].split(': ')[1]
//       : new Date().toISOString().split('T')[0];
//     const payload = {
//       user_whoRequested_id: parsedUserDetails.name || '',
//       group_id: selectedProjectId || '',
//       service_name_id: (selectedServiceTitle || '').replace(' ', '_'),
//       nested_component_id: 'Invoice',
//       invoice_id: data.id,
//       client_name: data.customerName,
//       filter_date: filterDate,
//       updated_invoice: invoiceData
//     };
//     if (invoice && (invoice.id || invoice.invoice_number)) {
//       dispatch(editInvoice(payload));
//     }
//     onSubmit(data);
//   };

//   return (
//     <LocalizationProvider dateAdapter={AdapterDateFns}>
//       <Box
//         sx={{
//           width: '100%',
//           maxWidth: '100%',
//           p: isSmallScreen ? 1 : 2,
//           borderRadius: 2
//         }}
//       >
//         <Typography
//           variant="h5"
//           sx={{
//             fontWeight: 'bold',
//             textAlign: 'center',
//             mb: 3,
//             fontSize: isSmallScreen ? '1.3rem' : '1.5rem',
//             color: 'primary.main'
//           }}
//         >
//           {invoice ? 'Edit Invoice' : 'Create Invoice'}
//         </Typography>

//         {loading && (
//           <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
//             <CircularProgress />
//           </Box>
//         )}

//         {error && (
//           <Alert severity="error" sx={{ mb: 2 }}>
//             {error}
//           </Alert>
//         )}

//         <form onSubmit={handleSubmit(onSubmitForm)}>
//           <Grid container spacing={2}>
//             <Grid item xs={12}>
//               <Typography
//                 variant="subtitle1"
//                 sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}
//               >
//                 Basic Information
//               </Typography>
//               <Box
//                 sx={{
//                   p: isSmallScreen ? 1 : 2,
//                   bgcolor: 'background.paper',
//                   borderRadius: 2,
//                   border: '1px solid',
//                   borderColor: 'divider'
//                 }}
//               >
//                 <Grid container spacing={1.5}>
//                   <Grid item xs={12} sm={6}>
//                     <Controller
//                       name="id"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="ID"
//                           error={!!errors.id}
//                           helperText={errors.id?.message}
//                           required
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={6}>
//                     <Controller
//                       name="invoiceNumber"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="Invoice Number"
//                           error={!!errors.invoiceNumber}
//                           helperText={errors.invoiceNumber?.message}
//                           required
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={6}>
//                     <Controller
//                       name="client_reference"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="Client Reference"
//                           error={!!errors.client_reference}
//                           helperText={errors.client_reference?.message}
//                           required
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={6}>
//                     <Controller
//                       name="registrationnumber"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="Registration Number"
//                           error={!!errors.registrationnumber}
//                           helperText={errors.registrationnumber?.message}
//                           required
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={6}>
//                     <Controller
//                       name="ndisnumber"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="NDIS Number"
//                           error={!!errors.ndisnumber}
//                           helperText={errors.ndisnumber?.message}
//                           required
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={6}>
//                     <Controller
//                       name="authorisedby"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="Authorised By"
//                           error={!!errors.authorisedby}
//                           helperText={errors.authorisedby?.message}
//                           required
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={6}>
//                     <Controller
//                       name="participantapproved"
//                       control={control}
//                       render={({ field }) => (
//                         <FormControl component="fieldset">
//                           <Box sx={{ display: 'flex', alignItems: 'center' }}>
//                             <Typography sx={{ mr: 2, fontSize: '0.875rem' }}>
//                               Participant Approved
//                             </Typography>
//                             <Switch
//                               checked={field.value}
//                               onChange={(e) => field.onChange(e.target.checked)}
//                             />
//                           </Box>
//                           {errors.participantapproved && (
//                             <Typography variant="caption" color="error">
//                               {errors.participantapproved.message}
//                             </Typography>
//                           )}
//                         </FormControl>
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={6}>
//                     <Controller
//                       name="inkindfundingprogram"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="In-Kind Funding Program"
//                           error={!!errors.inkindfundingprogram}
//                           helperText={errors.inkindfundingprogram?.message}
//                           required
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={6}>
//                     <Controller
//                       name="claimtype"
//                       control={control}
//                       render={({ field }) => (
//                         <FormControl fullWidth size="small" error={!!errors.claimtype}>
//                           <InputLabel sx={{ fontSize: '0.875rem' }}>Claim Type</InputLabel>
//                           <Select
//                             {...field}
//                             label="Claim Type"
//                             sx={{ fontSize: '0.875rem' }}
//                             onChange={(e: SelectChangeEvent<string>) =>
//                               field.onChange(e.target.value)
//                             }
//                           >
//                             <MenuItem value="Standard">Standard</MenuItem>
//                             <MenuItem value="Non-Standard">Non-Standard</MenuItem>
//                           </Select>
//                           {errors.claimtype && (
//                             <Typography variant="caption" color="error">
//                               {errors.claimtype.message}
//                             </Typography>
//                           )}
//                         </FormControl>
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={6}>
//                     <Controller
//                       name="gstcode"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="GST Code"
//                           error={!!errors.gstcode}
//                           helperText={errors.gstcode?.message}
//                           required
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={6}>
//                     <Controller
//                       name="supportnumber"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="Support Number"
//                           error={!!errors.supportnumber}
//                           helperText={errors.supportnumber?.message}
//                           required
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={6}>
//                     <Controller
//                       name="Support_Item_Name"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="Support Item Name"
//                           error={!!errors.Support_Item_Name}
//                           helperText={errors.Support_Item_Name?.message}
//                           required
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                 </Grid>
//               </Box>
//             </Grid>

//             <Grid item xs={12}>
//               <Typography
//                 variant="subtitle1"
//                 sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}
//               >
//                 Participant Information
//               </Typography>
//               <Box
//                 sx={{
//                   p: isSmallScreen ? 1 : 2,
//                   bgcolor: 'background.paper',
//                   borderRadius: 2,
//                   border: '1px solid',
//                   borderColor: 'divider'
//                 }}
//               >
//                 <Grid container spacing={1.5}>
//                   <Grid item xs={12} sm={6}>
//                     <Controller
//                       name="customerName"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="Customer Name"
//                           error={!!errors.customerName}
//                           helperText={errors.customerName?.message}
//                           required
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={6}>
//                     <Controller
//                       name="address"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="Address"
//                           error={!!errors.address}
//                           helperText={errors.address?.message}
//                           required
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={6}>
//                     <Controller
//                       name="cityStateZip"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="City, State, Zip"
//                           error={!!errors.cityStateZip}
//                           helperText={errors.cityStateZip?.message}
//                           required
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                 </Grid>
//               </Box>
//             </Grid>

//             <Grid item xs={12}>
//               <Typography
//                 variant="subtitle1"
//                 sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}
//               >
//                 Payment Details
//               </Typography>
//               <Box
//                 sx={{
//                   p: isSmallScreen ? 1 : 2,
//                   bgcolor: 'background.paper',
//                   borderRadius: 2,
//                   border: '1px solid',
//                   borderColor: 'divider'
//                 }}
//               >
//                 <Grid container spacing={1.5}>
//                   <Grid item xs={12} sm={6}>
//                     <Controller
//                       name="paymentDetails.BSB"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="BSB"
//                           error={!!errors.paymentDetails?.BSB}
//                           helperText={errors.paymentDetails?.BSB?.message}
//                           required
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={6}>
//                     <Controller
//                       name="paymentDetails.Account"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="Account Number"
//                           error={!!errors.paymentDetails?.Account}
//                           helperText={errors.paymentDetails?.Account?.message}
//                           required
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={6}>
//                     <Controller
//                       name="paymentDetails.ABN"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="ABN"
//                           error={!!errors.paymentDetails?.ABN}
//                           helperText={errors.paymentDetails?.ABN?.message}
//                           required
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={6}>
//                     <Controller
//                       name="paymentDetails.NDIS_Registration_Number"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="NDIS Registration Number"
//                           error={!!errors.paymentDetails?.NDIS_Registration_Number}
//                           helperText={errors.paymentDetails?.NDIS_Registration_Number?.message}
//                           required
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={6}>
//                     <Controller
//                       name="paymentDetails.Contact"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="Contact"
//                           error={!!errors.paymentDetails?.Contact}
//                           helperText={errors.paymentDetails?.Contact?.message}
//                           required
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                 </Grid>
//               </Box>
//             </Grid>

//             <Grid item xs={12}>
//               <Typography
//                 variant="subtitle1"
//                 sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}
//               >
//                 Dates
//               </Typography>
//               <Box
//                 sx={{
//                   p: isSmallScreen ? 1 : 2,
//                   bgcolor: 'background.paper',
//                   borderRadius: 2,
//                   border: '1px solid',
//                   borderColor: 'divider'
//                 }}
//               >
//                 <Grid container spacing={1.5}>
//                   <Grid item xs={12} sm={4}>
//                     <Controller
//                       name="date"
//                       control={control}
//                       render={({ field }) => (
//                         <DatePicker
//                           label="Issued Date"
//                           value={field.value ? new Date(field.value) : null}
//                           onChange={(date) =>
//                             field.onChange(date ? date.toISOString().split('T')[0] : '')
//                           }
//                           slotProps={{
//                             textField: {
//                               fullWidth: true,
//                               size: 'small',
//                               error: !!errors.date,
//                               helperText: errors.date?.message,
//                               required: true,
//                               sx: { '& .MuiInputBase-root': { fontSize: '0.875rem' } }
//                             }
//                           }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={4}>
//                     <Controller
//                       name="issuedPeriodDate_start"
//                       control={control}
//                       render={({ field }) => (
//                         <DatePicker
//                           label="Period Start"
//                           value={field.value ? new Date(field.value) : null}
//                           onChange={(date) =>
//                             field.onChange(date ? date.toISOString().split('T')[0] : '')
//                           }
//                           slotProps={{
//                             textField: {
//                               fullWidth: true,
//                               size: 'small',
//                               error: !!errors.issuedPeriodDate_start,
//                               helperText: errors.issuedPeriodDate_start?.message,
//                               required: true,
//                               sx: { '& .MuiInputBase-root': { fontSize: '0.875rem' } }
//                             }
//                           }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={4}>
//                     <Controller
//                       name="issuedPeriodDate_end"
//                       control={control}
//                       render={({ field }) => (
//                         <DatePicker
//                           label="Period End"
//                           value={field.value ? new Date(field.value) : null}
//                           onChange={(date) =>
//                             field.onChange(date ? date.toISOString().split('T')[0] : '')
//                           }
//                           slotProps={{
//                             textField: {
//                               fullWidth: true,
//                               size: 'small',
//                               error: !!errors.issuedPeriodDate_end,
//                               helperText: errors.issuedPeriodDate_end?.message,
//                               required: true,
//                               sx: { '& .MuiInputBase-root': { fontSize: '0.875rem' } }
//                             }
//                           }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12}>
//                     <Controller
//                       name="dueDate"
//                       control={control}
//                       render={({ field }) => (
//                         <DatePicker
//                           label="Due Date"
//                           value={field.value ? new Date(field.value) : null}
//                           onChange={(date) =>
//                             field.onChange(date ? date.toISOString().split('T')[0] : '')
//                           }
//                           slotProps={{
//                             textField: {
//                               fullWidth: true,
//                               size: 'small',
//                               error: !!errors.dueDate,
//                               helperText: errors.dueDate?.message,
//                               required: true,
//                               sx: { '& .MuiInputBase-root': { fontSize: '0.875rem' } }
//                             }
//                           }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                 </Grid>
//               </Box>
//             </Grid>

//             <Grid item xs={12}>
//               <Typography
//                 variant="subtitle1"
//                 sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}
//               >
//                 Invoice Items
//               </Typography>
//               <Box
//                 sx={{
//                   p: isSmallScreen ? 1 : 2,
//                   bgcolor: 'background.paper',
//                   borderRadius: 2,
//                   border: '1px solid',
//                   borderColor: 'divider'
//                 }}
//               >
//                 <Typography variant="caption" color="error">
//                   Note: Invoice items are required but not implemented in this form. Add fields as
//                   needed.
//                 </Typography>
//               </Box>
//             </Grid>

//             <Grid item xs={12}>
//               <Typography
//                 variant="subtitle1"
//                 sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}
//               >
//                 Shifts
//               </Typography>
//               <Box
//                 sx={{
//                   p: isSmallScreen ? 1 : 2,
//                   bgcolor: 'background.paper',
//                   borderRadius: 2,
//                   border: '1px solid',
//                   borderColor: 'divider'
//                 }}
//               >
//                 {fields.map((field, index) => (
//                   <FormGroup
//                     key={field.id}
//                     sx={{
//                       mb: 2,
//                       p: isSmallScreen ? 1 : 1.5,
//                       border: '1px solid',
//                       borderColor: 'divider',
//                       borderRadius: 1,
//                       bgcolor: 'background.default'
//                     }}
//                   >
//                     <Grid container spacing={1.5} alignItems="center">
//                       <Grid item xs={12}>
//                         <Controller
//                           name={`shifts.${index}.shift_title`}
//                           control={control}
//                           render={({ field }) => (
//                             <TextField
//                               {...field}
//                               fullWidth
//                               label="Shift Title"
//                               error={!!errors.shifts?.[index]?.shift_title}
//                               helperText={errors.shifts?.[index]?.shift_title?.message}
//                               size="small"
//                               sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                             />
//                           )}
//                         />
//                       </Grid>
//                       <Grid item xs={12} sm={6}>
//                         <Controller
//                           name={`shifts.${index}.supports_delivered_from`}
//                           control={control}
//                           render={({ field }) => (
//                             <DatePicker
//                               label="Supports Delivered From"
//                               value={field.value ? new Date(field.value) : null}
//                               onChange={(date) => {
//                                 const dateStr = date ? date.toISOString().split('T')[0] : '';
//                                 field.onChange(dateStr);
//                                 handleShiftChange(index, 'supports_delivered_from', dateStr);
//                               }}
//                               slotProps={{
//                                 textField: {
//                                   fullWidth: true,
//                                   size: 'small',
//                                   error: !!errors.shifts?.[index]?.supports_delivered_from,
//                                   helperText:
//                                     errors.shifts?.[index]?.supports_delivered_from?.message,
//                                   sx: { '& .MuiInputBase-root': { fontSize: '0.875rem' } }
//                                 }
//                               }}
//                             />
//                           )}
//                         />
//                       </Grid>
//                       <Grid item xs={12} sm={6}>
//                         <Controller
//                           name={`shifts.${index}.supports_delivered_to`}
//                           control={control}
//                           render={({ field }) => (
//                             <DatePicker
//                               label="Supports Delivered To"
//                               value={field.value ? new Date(field.value) : null}
//                               onChange={(date) => {
//                                 const dateStr = date ? date.toISOString().split('T')[0] : '';
//                                 field.onChange(dateStr);
//                                 handleShiftChange(index, 'supports_delivered_to', dateStr);
//                               }}
//                               slotProps={{
//                                 textField: {
//                                   fullWidth: true,
//                                   size: 'small',
//                                   error: !!errors.shifts?.[index]?.supports_delivered_to,
//                                   helperText:
//                                     errors.shifts?.[index]?.supports_delivered_to?.message,
//                                   sx: { '& .MuiInputBase-root': { fontSize: '0.875rem' } }
//                                 }
//                               }}
//                             />
//                           )}
//                         />
//                       </Grid>
//                       <Grid item xs={12} sm={6}>
//                         <Controller
//                           name={`shifts.${index}.issued_period_date_start`}
//                           control={control}
//                           render={({ field }) => (
//                             <DatePicker
//                               label="Period Start"
//                               value={field.value ? new Date(field.value) : null}
//                               onChange={(date) => {
//                                 const dateStr = date ? date.toISOString().split('T')[0] : '';
//                                 field.onChange(dateStr);
//                                 handleShiftChange(index, 'issued_period_date_start', dateStr);
//                               }}
//                               slotProps={{
//                                 textField: {
//                                   fullWidth: true,
//                                   size: 'small',
//                                   error: !!errors.shifts?.[index]?.issued_period_date_start,
//                                   helperText:
//                                     errors.shifts?.[index]?.issued_period_date_start?.message,
//                                   sx: { '& .MuiInputBase-root': { fontSize: '0.875rem' } }
//                                 }
//                               }}
//                             />
//                           )}
//                         />
//                       </Grid>
//                       <Grid item xs={12} sm={6}>
//                         <Controller
//                           name={`shifts.${index}.issued_period_date_end`}
//                           control={control}
//                           render={({ field }) => (
//                             <DatePicker
//                               label="Period End"
//                               value={field.value ? new Date(field.value) : null}
//                               onChange={(date) => {
//                                 const dateStr = date ? date.toISOString().split('T')[0] : '';
//                                 field.onChange(dateStr);
//                                 handleShiftChange(index, 'issued_period_date_end', dateStr);
//                               }}
//                               slotProps={{
//                                 textField: {
//                                   fullWidth: true,
//                                   size: 'small',
//                                   error: !!errors.shifts?.[index]?.issued_period_date_end,
//                                   helperText:
//                                     errors.shifts?.[index]?.issued_period_date_end?.message,
//                                   sx: { '& .MuiInputBase-root': { fontSize: '0.875rem' } }
//                                 }
//                               }}
//                             />
//                           )}
//                         />
//                       </Grid>
//                       <Grid item xs={6} sm={3}>
//                         <Controller
//                           name={`shifts.${index}.hours`}
//                           control={control}
//                           render={({ field }) => (
//                             <TextField
//                               {...field}
//                               fullWidth
//                               label="Hours"
//                               type="number"
//                               error={!!errors.shifts?.[index]?.hours}
//                               helperText={errors.shifts?.[index]?.hours?.message}
//                               size="small"
//                               inputProps={{ step: '0.01', min: 0 }}
//                               onChange={(e) => {
//                                 const value = parseFloat(e.target.value);
//                                 field.onChange(value);
//                                 handleShiftChange(index, 'hours', value);
//                               }}
//                               sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                             />
//                           )}
//                         />
//                       </Grid>
//                       <Grid item xs={6} sm={3}>
//                         <Controller
//                           name={`shifts.${index}.unit_price`}
//                           control={control}
//                           render={({ field }) => (
//                             <TextField
//                               {...field}
//                               fullWidth
//                               label="Unit Price"
//                               type="number"
//                               error={!!errors.shifts?.[index]?.unit_price}
//                               helperText={errors.shifts?.[index]?.unit_price?.message}
//                               size="small"
//                               inputProps={{ step: '0.01', min: 0 }}
//                               onChange={(e) => {
//                                 const value = parseFloat(e.target.value);
//                                 field.onChange(value);
//                                 handleShiftChange(index, 'unit_price', value);
//                               }}
//                               sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                             />
//                           )}
//                         />
//                       </Grid>
//                       <Grid item xs={6} sm={3}>
//                         <Controller
//                           name={`shifts.${index}.price`}
//                           control={control}
//                           render={({ field }) => (
//                             <TextField
//                               {...field}
//                               fullWidth
//                               label="Price"
//                               type="number"
//                               error={!!errors.shifts?.[index]?.price}
//                               helperText={errors.shifts?.[index]?.price?.message}
//                               size="small"
//                               inputProps={{ step: '0.01', min: 0 }}
//                               sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                             />
//                           )}
//                         />
//                       </Grid>
//                       <Grid item xs={6} sm={3}>
//                         <Controller
//                           name={`shifts.${index}.quantity`}
//                           control={control}
//                           render={({ field }) => (
//                             <TextField
//                               {...field}
//                               fullWidth
//                               label="Quantity"
//                               type="number"
//                               error={!!errors.shifts?.[index]?.quantity}
//                               helperText={errors.shifts?.[index]?.quantity?.message}
//                               size="small"
//                               inputProps={{ min: 1 }}
//                               onChange={(e) => {
//                                 const value = parseFloat(e.target.value);
//                                 field.onChange(value);
//                                 handleShiftChange(index, 'quantity', value);
//                               }}
//                               sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                             />
//                           )}
//                         />
//                       </Grid>
//                       <Grid item xs={6} sm={3}>
//                         <Controller
//                           name={`shifts.${index}.Total_amount`}
//                           control={control}
//                           render={({ field }) => (
//                             <TextField
//                               {...field}
//                               fullWidth
//                               label="Total Amount"
//                               type="number"
//                               InputProps={{ readOnly: true }}
//                               error={!!errors.shifts?.[index]?.Total_amount}
//                               helperText={errors.shifts?.[index]?.Total_amount?.message}
//                               size="small"
//                               sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                             />
//                           )}
//                         />
//                       </Grid>
//                       <Grid item xs={12} sm={6}>
//                         <Controller
//                           name={`shifts.${index}.support_number`}
//                           control={control}
//                           render={({ field }) => (
//                             <TextField
//                               {...field}
//                               fullWidth
//                               label="Support Number"
//                               error={!!errors.shifts?.[index]?.support_number}
//                               helperText={errors.shifts?.[index]?.support_number?.message}
//                               size="small"
//                               sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                             />
//                           )}
//                         />
//                       </Grid>
//                       <Grid item xs={12} sm={6}>
//                         <Controller
//                           name={`shifts.${index}.claimreference`}
//                           control={control}
//                           render={({ field }) => (
//                             <TextField
//                               {...field}
//                               fullWidth
//                               label="Claim Reference"
//                               error={!!errors.shifts?.[index]?.claimreference}
//                               helperText={errors.shifts?.[index]?.claimreference?.message}
//                               size="small"
//                               sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                             />
//                           )}
//                         />
//                       </Grid>
//                       <Grid item xs={12}>
//                         <Controller
//                           name={`shifts.${index}.support_item_name`}
//                           control={control}
//                           render={({ field }) => (
//                             <TextField
//                               {...field}
//                               fullWidth
//                               label="Support Item Name"
//                               error={!!errors.shifts?.[index]?.support_item_name}
//                               helperText={errors.shifts?.[index]?.support_item_name?.message}
//                               size="small"
//                               sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                             />
//                           )}
//                         />
//                       </Grid>
//                       <Grid item xs={12} sx={{ textAlign: 'center' }}>
//                         {fields.length > 1 && (
//                           <IconButton
//                             onClick={() => removeShift(index)}
//                             color="error"
//                             size="small"
//                             sx={{
//                               border: '1px solid',
//                               borderColor: 'error.main',
//                               borderRadius: 1
//                             }}
//                           >
//                             <Remove fontSize="small" />
//                           </IconButton>
//                         )}
//                       </Grid>
//                     </Grid>
//                   </FormGroup>
//                 ))}
//                 {errors.shifts && (
//                   <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
//                     {errors.shifts.message}
//                   </Typography>
//                 )}
//                 <Button
//                   startIcon={<Add />}
//                   onClick={addShift}
//                   variant="outlined"
//                   size="small"
//                   fullWidth={isSmallScreen}
//                   sx={{
//                     mt: 1,
//                     fontSize: '0.75rem',
//                     textTransform: 'none'
//                   }}
//                 >
//                   Add Shift
//                 </Button>
//               </Box>
//             </Grid>

//             <Grid item xs={12}>
//               <Typography
//                 variant="subtitle1"
//                 sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}
//               >
//                 Summary
//               </Typography>
//               <Box
//                 sx={{
//                   p: isSmallScreen ? 1 : 2,
//                   bgcolor: 'background.paper',
//                   borderRadius: 2,
//                   border: '1px solid',
//                   borderColor: 'divider'
//                 }}
//               >
//                 <Grid container spacing={1.5}>
//                   <Grid item xs={12} sm={4}>
//                     <Controller
//                       name="summary.total_hours"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="Total Hours"
//                           value={summary.total_hours.toFixed(2)}
//                           InputProps={{ readOnly: true }}
//                           error={!!errors.summary?.total_hours}
//                           helperText={errors.summary?.total_hours?.message}
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={4}>
//                     <Controller
//                       name="summary.total_quantity"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="Total Quantity"
//                           value={summary.total_quantity}
//                           InputProps={{ readOnly: true }}
//                           error={!!errors.summary?.total_quantity}
//                           helperText={errors.summary?.total_quantity?.message}
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                   <Grid item xs={12} sm={4}>
//                     <Controller
//                       name="summary.total_amount_due"
//                       control={control}
//                       render={({ field }) => (
//                         <TextField
//                           {...field}
//                           fullWidth
//                           label="Total Amount Due"
//                           value={summary.total_amount_due.toFixed(2)}
//                           InputProps={{ readOnly: true }}
//                           error={!!errors.summary?.total_amount_due}
//                           helperText={errors.summary?.total_amount_due?.message}
//                           size="small"
//                           sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                         />
//                       )}
//                     />
//                   </Grid>
//                 </Grid>
//               </Box>
//             </Grid>

//             <Grid item xs={12}>
//               <Typography
//                 variant="subtitle1"
//                 sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}
//               >
//                 Avatar
//               </Typography>
//               <Box
//                 sx={{
//                   p: isSmallScreen ? 1 : 2,
//                   bgcolor: 'background.paper',
//                   borderRadius: 2,
//                   border: '1px solid',
//                   borderColor: 'divider'
//                 }}
//               >
//                 <Controller
//                   name="avatar"
//                   control={control}
//                   render={({ field }) => (
//                     <TextField
//                       {...field}
//                       fullWidth
//                       label="Avatar URL"
//                       error={!!errors.avatar}
//                       helperText={errors.avatar?.message}
//                       size="small"
//                       sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
//                     />
//                   )}
//                 />
//               </Box>
//             </Grid>

//             <Grid item xs={12}>
//               <Box
//                 sx={{
//                   display: 'flex',
//                   justifyContent: 'flex-end',
//                   gap: 1.5,
//                   mt: 1,
//                   flexDirection: isSmallScreen ? 'column' : 'row'
//                 }}
//               >
//                 <Button
//                   variant="outlined"
//                   onClick={onCancel}
//                   size={isSmallScreen ? 'medium' : 'small'}
//                   fullWidth={isSmallScreen}
//                   sx={{
//                     fontSize: '0.75rem',
//                     textTransform: 'none'
//                   }}
//                 >
//                   Cancel
//                 </Button>
//                 <Button
//                   type="submit"
//                   variant="contained"
//                   color="primary"
//                   size={isSmallScreen ? 'medium' : 'small'}
//                   fullWidth={isSmallScreen}
//                   sx={{
//                     fontSize: '0.75rem',
//                     textTransform: 'none'
//                   }}
//                   disabled={loading}
//                 >
//                   {invoice ? 'Update Invoice' : 'Create Invoice'}
//                 </Button>
//               </Box>
//             </Grid>
//           </Grid>
//         </form>
//       </Box>
//     </LocalizationProvider>
//   );
// };

// // Updated mapToUIMappedInvoice to handle optional fields from Invoice
// export const mapToUIMappedInvoice = (invoice: Invoice): UIMappedInvoice => ({
//   id: invoice.id || '',
//   invoiceNumber: invoice.invoice_number || '',
//   customerName: invoice.customer_name || '',
//   address: invoice.address || '',
//   cityStateZip: invoice.city_state_zip || '',
//   date: invoice.issued_date || new Date().toISOString().split('T')[0],
//   issuedPeriodDate_start: invoice.issued_period_date_start || '',
//   issuedPeriodDate_end: invoice.issued_period_date_end || '',
//   dueDate:
//     invoice.due_date ||
//     new Date(new Date().setDate(new Date().getDate() + 30)).toISOString().split('T')[0],
//   totalAmount: invoice.Amount || 0,
//   status: invoice.summary?.total_amount_due === 0 ? 'paid' : 'unpaid',
//   paymentDetails: {
//     BSB: invoice.bsb || '',
//     Account: invoice.account || '',
//     ABN: invoice.abn || '',
//     NDIS_Registration_Number: invoice.registration_number || '',
//     Contact: invoice.contact || ''
//   },
//   invoiceItems: [], // Note: Invoice type does not provide invoiceItems, so default to empty array
//   client_reference: invoice.client_reference || '',
//   registrationnumber: invoice.registration_number || '',
//   ndisnumber: invoice.ndis_number || '',
//   authorisedby: invoice.authorised_by || '',
//   participantapproved: invoice.participant_approved || false,
//   inkindfundingprogram: invoice.in_kind_funding_program || '',
//   claimtype: invoice.claim_type || '',
//   gstcode: invoice.gst_code || '',
//   supportnumber: '',
//   Support_Item_Name: '',
//   shifts:
//     invoice.shifts?.map((shift) => ({
//       shift_title: shift.shift_title || '',
//       supports_delivered_from: shift.supports_delivered_from || '',
//       supports_delivered_to: shift.supports_delivered_to || '',
//       issued_period_date_start: shift.issued_period_date_start || '',
//       issued_period_date_end: shift.issued_period_date_end || '',
//       support_number: shift.support_number || '',
//       claimreference: shift.claimreference || '',
//       support_item_name: shift.support_item_name || '',
//       hours: shift.hours || 0,
//       unit_price: shift.unit_price || 0,
//       quantity: shift.quantity || 1,
//       price: shift.price || 0,
//       Total_amount: shift.Total_amount || 0
//     })) || [],
//   summary: {
//     total_hours: invoice.summary?.total_hours || 0,
//     total_quantity: invoice.summary?.total_quantity || 0,
//     total_amount_due: invoice.summary?.total_amount_due || 0
//   },
//   avatar: invoice.avatar || ''
// });

// export const mapToInvoice = (uiInvoice: UIMappedInvoice): Invoice => ({
//   id: uiInvoice.id,
//   invoice_number: uiInvoice.invoiceNumber,
//   customer_name: uiInvoice.customerName,
//   address: uiInvoice.address,
//   city_state_zip: uiInvoice.cityStateZip,
//   issued_date: uiInvoice.date,
//   issued_period_date_start: uiInvoice.issuedPeriodDate_start,
//   issued_period_date_end: uiInvoice.issuedPeriodDate_end,
//   due_date: uiInvoice.dueDate,
//   bsb: uiInvoice.paymentDetails.BSB,
//   account: uiInvoice.paymentDetails.Account,
//   abn: uiInvoice.paymentDetails.ABN,
//   contact: uiInvoice.paymentDetails.Contact,
//   gst_code: uiInvoice.gstcode,
//   client_reference: uiInvoice.client_reference,
//   registration_number: uiInvoice.registrationnumber,
//   ndis_number: uiInvoice.ndisnumber,
//   authorised_by: uiInvoice.authorisedby,
//   participant_approved: uiInvoice.participantapproved,
//   in_kind_funding_program: uiInvoice.inkindfundingprogram,
//   claim_type: uiInvoice.claimtype,
//   shifts: uiInvoice.shifts.map((shift) => ({
//     shift_title: shift.shift_title,
//     supports_delivered_from: shift.supports_delivered_from,
//     supports_delivered_to: shift.supports_delivered_to,
//     issued_period_date_start: shift.issued_period_date_start,
//     issued_period_date_end: shift.issued_period_date_end,
//     support_number: shift.support_number,
//     claimreference: shift.claimreference,
//     support_item_name: shift.support_item_name,
//     hours: shift.hours,
//     unit_price: shift.unit_price,
//     quantity: shift.quantity,
//     price: shift.price,
//     Total_amount: shift.Total_amount
//   })),
//   summary: {
//     total_hours: uiInvoice.summary.total_hours,
//     total_quantity: uiInvoice.summary.total_quantity,
//     total_amount_due: uiInvoice.summary.total_amount_due
//   },
//   Hours: uiInvoice.summary.total_hours,
//   Amount: uiInvoice.summary.total_amount_due,
//   avatar: uiInvoice.avatar
// });

// export default InvoiceForm;
